// Guided venue setup and the Go-live gate (F-HQ-03; ADR-0047; spec/14 §3.2, spec/13 §13.1):
//
//   GET    /v1/organizations/{o}/setup                                settings:read   the organisation's steps and each of its venues'
//   PUT    /v1/organizations/{o}/setup/skips/{step}                   settings:all    skip the organisation's suggested step
//   DELETE /v1/organizations/{o}/setup/skips/{step}                   settings:all    take that skip back
//   GET    /v1/organizations/{o}/venues/{v}/setup                     settings:read   one venue's steps
//   PUT    /v1/organizations/{o}/venues/{v}/setup/skips/{step}        venues:update   skip a suggested step
//   DELETE /v1/organizations/{o}/venues/{v}/setup/skips/{step}        venues:update   take a skip back
//   GET    /v1/hq/setup?organizationId=a,b                            hq:organizations  HQ: where each organisation stands
//
// **Every step is computed from the venue's real state when it is read** — opening hours on the venue record, a pass
// on sale, a PC paired, a receipt printer added, a session run — never from a box somebody ticked. The one thing
// stored is a **skip**: an owner or manager may skip a *suggested* step ("we take no online payments"), and the skip is
// kept per venue with who and when, and audited. An *essential* step cannot be skipped. The organisation's own
// suggested step — its business details, which only a GST registration needs — is skipped the same way by whoever may
// change them (`settings:all`, ADR-0046).
//
// **The essentials are the Go-live gate** (the product owner, 2026-10-08): a venue in `setup` goes `live` only when its
// opening hours are set, players have a pass they can buy (step id `price`, "How players pay"; ADR-0058), and a PC is
// paired. `PATCH …/venues/{v}` with
// `{ status: 'live' }` from `setup` is otherwise `409 venue-not-ready`, whose `missingSteps` names what is missing.
// Only that move is gated: a venue already `live` or `paused` pauses and resumes as before.
//
// **HQ reads it across organisations** the way it reads venues (spec/14 §3.3, spec/07 §11.2): one platform row for the
// read and one row in the log of every organisation it returns.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { MemberIdSchema, OrganizationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { typeIdPattern } from "../ids/typeid.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_ORGANIZATIONS, SETTINGS_ALL, SETTINGS_READ, VENUES_UPDATE } from "../permissions/index.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { ESSENTIAL_ORGANIZATION_SETUP_STEPS, ESSENTIAL_VENUE_SETUP_STEPS, ORGANIZATION_SETUP_STEPS, SUGGESTED_ORGANIZATION_SETUP_STEPS, SUGGESTED_VENUE_SETUP_STEPS, VENUE_SETUP_STEPS, } from "../primitives/setup-steps.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { VenueSlugSchema, VenueStatusSchema } from "./venues.js";
// ---------------------------------------------------------------------------------------------
// The steps (their ids are `primitives/setup-steps.ts`, plain values the console can import)
// ---------------------------------------------------------------------------------------------
export const OrganizationSetupStepIdSchema = z.enum(ORGANIZATION_SETUP_STEPS).openapi('OrganizationSetupStepId', {
    description: 'One of an organisation’s own setup steps. Essential: `firstVenue`. Suggested: `businessDetails`.',
});
export const SuggestedOrganizationSetupStepIdSchema = z.enum(SUGGESTED_ORGANIZATION_SETUP_STEPS).openapi('SuggestedOrganizationSetupStepId', {
    description: 'An organisation setup step that may be skipped.',
});
export const VenueSetupStepIdSchema = z.enum(VENUE_SETUP_STEPS).openapi('VenueSetupStepId', {
    description: 'One of a venue’s setup steps. Essential: `openingHours`, `price`, `stationPaired` (the Go-live gate). Suggested: the rest. ' +
        '`price` is "How players pay": done when a pass is on sale at the venue (ADR-0058).',
});
export const EssentialVenueSetupStepIdSchema = z.enum(ESSENTIAL_VENUE_SETUP_STEPS).openapi('EssentialVenueSetupStepId', {
    description: 'A venue setup step Go live waits on: `openingHours`, `price`, `stationPaired`.',
});
export const SuggestedVenueSetupStepIdSchema = z.enum(SUGGESTED_VENUE_SETUP_STEPS).openapi('SuggestedVenueSetupStepId', {
    description: 'A venue setup step that may be skipped.',
});
/** Whether a step is essential (cannot be skipped; a venue's gate Go live, an organisation's first venue) or suggested. */
export const SetupStepKindSchema = z.enum(['essential', 'suggested']).openapi('SetupStepKind');
/**
 * `done` — the venue's state says so (a skipped step that is later done reads `done`); `skipped` — a suggested step the
 * venue chose to skip; `todo` — neither.
 */
export const SetupStepStatusSchema = z.enum(['done', 'todo', 'skipped']).openapi('SetupStepStatus');
/** Who skipped a step, and when. The audit log has the rest (`hq.setup.skip`). */
export const SetupSkipSchema = z
    .object({
    at: IsoDateTimeSchema,
    /** The member who skipped it; null for a caller who was not a member. */
    by: MemberIdSchema.nullable(),
})
    .openapi('SetupSkip');
export const OrganizationSetupStepSchema = z
    .object({
    id: OrganizationSetupStepIdSchema,
    kind: SetupStepKindSchema,
    status: SetupStepStatusSchema,
    /** Present while the step is skipped (and kept, quietly, if it is done since). */
    skip: SetupSkipSchema.nullable(),
})
    .openapi('OrganizationSetupStep');
export const VenueSetupStepSchema = z
    .object({
    id: VenueSetupStepIdSchema,
    kind: SetupStepKindSchema,
    status: SetupStepStatusSchema,
    /** Present while the step is skipped (and kept, quietly, if it is done since). */
    skip: SetupSkipSchema.nullable(),
})
    .openapi('VenueSetupStep');
/** How far along: essentials done of total, suggested settled (done or skipped) of total. */
export const SetupProgressSchema = z
    .object({
    essentialsDone: z.int().min(0),
    essentialsTotal: z.int().min(0),
    suggestedSettled: z.int().min(0),
    suggestedTotal: z.int().min(0),
    /** Steps neither done nor skipped. */
    left: z.int().min(0),
})
    .openapi('SetupProgress');
export const VenueSetupSchema = z
    .object({
    venueId: VenueIdSchema,
    name: z.string(),
    slug: VenueSlugSchema,
    status: VenueStatusSchema,
    steps: z.array(VenueSetupStepSchema),
    progress: SetupProgressSchema,
    /**
     * The essentials still missing, in order — what `PATCH …/venues/{v}` `{ status: 'live' }` would be refused for.
     * Empty when the venue may go live (or already has).
     */
    missingEssentials: z.array(EssentialVenueSetupStepIdSchema),
})
    .openapi('VenueSetup', { description: 'A venue’s setup steps, each computed from its state when read (ADR-0047).' });
/**
 * Which of the organisation's business details its record holds — never the values, which a manager reading this has
 * no need of. The legal name and registered address make the (suggested) step done; PAN is optional (the product owner,
 * 2026-09-22).
 */
export const OrganizationBusinessDetailsStateSchema = z
    .object({
    legalName: z.boolean(),
    registeredAddress: z.boolean(),
    pan: z.boolean(),
})
    .openapi('OrganizationBusinessDetailsState');
export const OrganizationSetupSchema = z
    .object({
    organizationId: OrganizationIdSchema,
    steps: z.array(OrganizationSetupStepSchema),
    /** What the business-details step is read from: which details are on record. */
    businessDetails: OrganizationBusinessDetailsStateSchema,
    /** The venues the caller works at, oldest first; never an archived one. */
    venues: z.array(VenueSetupSchema),
    /** Whether any venue of the organisation is live or paused: it has opened for business. */
    anyVenueOpen: z.boolean(),
})
    .openapi('OrganizationSetup', { description: 'An organisation’s own setup steps and each of its venues’ (ADR-0047).' });
// ---------------------------------------------------------------------------------------------
// HQ's view (spec/14 §3.2)
// ---------------------------------------------------------------------------------------------
/** An organisation id list: comma-separated, at most 100 — the page of organisations HQ has loaded. */
export const HQ_SETUP_MAX_ORGANIZATIONS = 100;
const ONE_ORGANIZATION_ID = typeIdPattern('org').source.slice(1, -1);
const ORGANIZATION_ID_LIST = new RegExp(`^${ONE_ORGANIZATION_ID}(?:,${ONE_ORGANIZATION_ID}){0,${HQ_SETUP_MAX_ORGANIZATIONS - 1}}$`);
export const HqSetupQuerySchema = z
    .object({
    organizationId: z
        .string()
        .regex(ORGANIZATION_ID_LIST, `Comma-separated organisation ids, at most ${HQ_SETUP_MAX_ORGANIZATIONS}`)
        .openapi({ description: 'The organisations to read, comma-separated: at most 100.', example: 'org_01k5znw4hmfay8vvr14d2pf2db' }),
})
    .strict()
    .openapi('HqSetupQuery');
/** The organisation ids an `organizationId` list names, once each. */
export function parseOrganizationIdList(value) {
    return [...new Set(value.split(','))];
}
/** Where an organisation stands: `live` once a venue has opened, `setup` while its venues are setting up, `no_venue` before its first. */
export const HqSetupStateSchema = z.enum(['live', 'setup', 'no_venue']).openapi('HqSetupState');
/** An essential step HQ names as next: the organisation's first venue, or one of a venue's essentials. */
export const HqSetupNextStepSchema = z.enum([...ESSENTIAL_ORGANIZATION_SETUP_STEPS, ...ESSENTIAL_VENUE_SETUP_STEPS]).openapi('HqSetupNextStep');
export const HqVenueSetupSchema = z
    .object({
    venueId: VenueIdSchema,
    name: z.string(),
    status: VenueStatusSchema,
    essentialsDone: z.int().min(0),
    essentialsTotal: z.int().min(0),
    missingEssentials: z.array(EssentialVenueSetupStepIdSchema),
})
    .openapi('HqVenueSetup');
export const HqOrganizationSetupSchema = z
    .object({
    organizationId: OrganizationIdSchema,
    state: HqSetupStateSchema,
    /**
     * The organisation's essential (a first venue) and those of its venue nearest to ready, counted together ("3 of
     * 4"): what is left before it can open. Suggested steps are never counted. Both equal once it is `live`.
     */
    essentialsDone: z.int().min(0),
    essentialsTotal: z.int().min(0),
    /** The first essential still missing — the organisation's own first, then that venue's; absent when none is. */
    nextStep: HqSetupNextStepSchema.optional(),
    /** Its venues, oldest first, archived ones left out. */
    venues: z.array(HqVenueSetupSchema),
})
    .openapi('HqOrganizationSetup', { description: 'Where one organisation stands in its setup (spec/14 §3.2).' });
export const HqOrganizationSetupListSchema = z
    .object({
    /** One per organisation asked for that exists, in the order asked. */
    data: z.array(HqOrganizationSetupSchema),
})
    .openapi('HqOrganizationSetupList');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const SkipParamsSchema = VenueScopeParamsSchema.extend({ step: SuggestedVenueSetupStepIdSchema }).openapi('VenueSetupSkipParams');
const OrganizationSkipParamsSchema = OrganizationScopeParamsSchema.extend({ step: SuggestedOrganizationSetupStepIdSchema }).openapi('OrganizationSetupSkipParams');
const forbidden = problemResponse('The caller lacks the permission this route names.', 'forbidden');
const notFound = problemResponse('No such organisation or venue, or not one you work at.', 'not-found');
export const getOrganizationSetupRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/setup',
    operationId: 'getOrganizationSetup',
    tags: ['setup'],
    summary: 'The organisation’s setup',
    description: 'Its own steps (a first venue; business details, suggested) and, for each venue the caller works at, its steps — each ' +
        'computed from the state it names when read. Requires `settings:read`.',
    [PERMISSION_EXTENSION]: SETTINGS_READ,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(OrganizationSetupSchema, 'Where the organisation stands.'),
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getVenueSetupRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/setup',
    operationId: 'getVenueSetup',
    tags: ['setup'],
    summary: 'A venue’s setup',
    description: 'Its steps, essentials first, each computed from the venue’s state when read, with the essentials still missing ' +
        'before it can go live. Requires `settings:read`.',
    [PERMISSION_EXTENSION]: SETTINGS_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(VenueSetupSchema, 'Where the venue stands.'),
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const skipVenueSetupStepRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/setup/skips/{step}',
    operationId: 'skipVenueSetupStep',
    tags: ['setup'],
    summary: 'Skip a suggested setup step',
    description: 'Marks a suggested step skipped at this venue, with who and when; skipping one already skipped changes nothing. ' +
        'An essential step cannot be skipped (it is not a value of `step`). Audited (`hq.setup.skip`). Requires ' +
        '`venues:update`.',
    [PERMISSION_EXTENSION]: VENUES_UPDATE,
    request: { params: SkipParamsSchema },
    responses: {
        200: jsonResponse(VenueSetupSchema, 'The venue’s setup now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        409: problemResponse('The venue is archived.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const unskipVenueSetupStepRoute = createRoute({
    method: 'delete',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/setup/skips/{step}',
    operationId: 'unskipVenueSetupStep',
    tags: ['setup'],
    summary: 'Take back a skipped setup step',
    description: 'The step is back on the list as it stands. Taking back one that was never skipped changes nothing. Audited ' +
        '(`hq.setup.unskip`). Requires `venues:update`.',
    [PERMISSION_EXTENSION]: VENUES_UPDATE,
    request: { params: SkipParamsSchema },
    responses: {
        200: jsonResponse(VenueSetupSchema, 'The venue’s setup now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        409: problemResponse('The venue is archived.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const skipOrganizationSetupStepRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/setup/skips/{step}',
    operationId: 'skipOrganizationSetupStep',
    tags: ['setup'],
    summary: 'Skip the organisation’s suggested setup step',
    description: 'Marks the organisation’s suggested step skipped, with who and when; skipping one already skipped changes nothing. ' +
        'Its essential step (a first venue) cannot be skipped. Audited (`hq.setup.skip`). Requires `settings:all`.',
    [PERMISSION_EXTENSION]: SETTINGS_ALL,
    request: { params: OrganizationSkipParamsSchema },
    responses: {
        200: jsonResponse(OrganizationSetupSchema, 'The organisation’s setup now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const unskipOrganizationSetupStepRoute = createRoute({
    method: 'delete',
    path: '/v1/organizations/{organizationId}/setup/skips/{step}',
    operationId: 'unskipOrganizationSetupStep',
    tags: ['setup'],
    summary: 'Take back the organisation’s skipped setup step',
    description: 'The step is back on the list as it stands. Taking back one that was never skipped changes nothing. Audited ' +
        '(`hq.setup.unskip`). Requires `settings:all`.',
    [PERMISSION_EXTENSION]: SETTINGS_ALL,
    request: { params: OrganizationSkipParamsSchema },
    responses: {
        200: jsonResponse(OrganizationSetupSchema, 'The organisation’s setup now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listHqSetupRoute = createRoute({
    method: 'get',
    path: '/v1/hq/setup',
    operationId: 'listHqSetup',
    tags: ['hq'],
    summary: 'Where organisations stand in their setup',
    description: 'For each organisation named (at most 100), whether it is live, setting up or has no venue yet, its essentials ' +
        'done, the next one missing, and each venue’s. The read is audited: one platform-level row, and one row in the ' +
        'log of every organisation returned. Platform scope: requires `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { query: HqSetupQuerySchema },
    responses: {
        200: jsonResponse(HqOrganizationSetupListSchema, 'The organisations’ setup, in the order asked.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const SETUP_ROUTES = [
    getOrganizationSetupRoute,
    skipOrganizationSetupStepRoute,
    unskipOrganizationSetupStepRoute,
    getVenueSetupRoute,
    skipVenueSetupStepRoute,
    unskipVenueSetupStepRoute,
    listHqSetupRoute,
];
//# sourceMappingURL=setup.js.map